import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import {
  useCreateUser,
  useDeleteUser,
  useResetPassword,
  useRoles,
  useTeams,
  useUpdateUser,
  useUsers,
} from '../api/hooks';
import type { CrmUser } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { EmptyState, ErrorBanner, Modal, TableSkeleton, relativeTime } from '../components/ui';
import { UserPermissionsModal } from './UserPermissionsModal';
import { RecentReports, SendReportModal } from './SendReportModal';

const SCOPE_HELP: Record<string, string> = {
  own: 'Sees only leads assigned to them',
  team: "Sees their teams' leads",
  all: 'Sees every lead in the organisation',
};

/** Generates a readable initial password so admins don't invent weak ones. */
function suggestPassword(): string {
  const words = ['Falcon', 'Meadow', 'Cobalt', 'Harbour', 'Quartz', 'Lantern', 'Copper', 'Willow'];
  const w = () => words[Math.floor(Math.random() * words.length)]!;
  return `${w()}-${w()}-${Math.floor(1000 + Math.random() * 9000)}`;
}

export function UsersPage() {
  const { user: me } = useAuth();
  const { data: users = [], isPending } = useUsers(true);
  const { data: roles = [] } = useRoles();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CrmUser | null>(null);
  const [resetting, setResetting] = useState<CrmUser | null>(null);
  // Performance reports: who is ticked, and who the send dialog is open for.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reportFor, setReportFor] = useState<CrmUser[] | null>(null);
  const [permissionsFor, setPermissionsFor] = useState<CrmUser | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<CrmUser | null>(null);
  const [error, setError] = useState<unknown>(null);

  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  async function toggleActive(user: CrmUser) {
    setError(null);
    try {
      await updateUser.mutateAsync({ id: user.id, is_active: !user.is_active });
      setConfirmToggle(null);
    } catch (err) {
      setError(err);
      setConfirmToggle(null);
    }
  }

  const activeCount = users.filter((u) => u.is_active).length;

  // Reports go to active team specialists and managers only — the server
  // refuses anyone else, so they are not offered.
  const reportable = (u: CrmUser) =>
    u.is_active && (u.role?.key === 'counsellor' || u.role?.key === 'manager' || u.role?.key === 'team_lead');
  const eligible = users.filter(reportable);
  const chosen = eligible.filter((u) => selected.has(u.id));
  const allChosen = eligible.length > 0 && chosen.length === eligible.length;
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <p className="sub">
            {activeCount} active {activeCount === 1 ? 'user' : 'users'} · a user's role decides what
            they can do, and which leads they can see
          </p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Add user</button>
        </div>
      </div>

      <ErrorBanner error={error} />

      {chosen.length > 0 && (
        <div className="bulk-bar">
          <span className="count">{chosen.length} selected</span>
          <div className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={() => setSelected(new Set())}>Clear</button>
          <button className="btn btn-sm btn-primary" onClick={() => setReportFor(chosen)}>
            Send performance report ({chosen.length})
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <input
                  type="checkbox"
                  aria-label="Select every team specialist and manager"
                  title="Select every team specialist and manager"
                  checked={allChosen}
                  disabled={eligible.length === 0}
                  onChange={() => setSelected(allChosen ? new Set() : new Set(eligible.map((u) => u.id)))}
                />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Sees</th>
              <th>Teams</th>
              <th className="num">Leads</th>
              <th>Last login</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>

          {isPending ? (
            <TableSkeleton cols={10} />
          ) : (
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={user.is_active ? undefined : { opacity: 0.55 }}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${user.full_name}`}
                      checked={selected.has(user.id)}
                      disabled={!reportable(user)}
                      title={reportable(user) ? undefined : 'Performance reports are for active team specialists and managers'}
                      onChange={() => toggle(user.id)}
                    />
                  </td>
                  <td>
                    <strong>{user.full_name}</strong>
                    {user.id === me?.id && <span className="tag" style={{ marginLeft: 6 }}>you</span>}
                  </td>
                  <td className="mono">{user.email}</td>
                  <td>{user.role?.name ?? <span style={{ color: 'var(--faint)' }}>No role</span>}</td>
                  <td title={SCOPE_HELP[user.data_scope]}>
                    <span className="chip chip-plain">{user.data_scope}</span>
                  </td>
                  <td className="truncate">
                    {/* A manager runs a team without being on its roster, so
                        showing only team_members left them looking unattached. */}
                    {user.manages.map((t) => (
                      <span className="tag" key={t.id} title={`Manages ${t.name}`}>
                        {t.name} <span style={{ opacity: 0.7 }}>(manages)</span>
                      </span>
                    ))}
                    {user.teams.map((t) => (
                      <span className="tag" key={t.id}>{t.name}</span>
                    ))}
                    {user.teams.length === 0 && user.manages.length === 0 && '—'}
                  </td>
                  <td className="num">{user.lead_count}</td>
                  <td>{user.last_login_at ? relativeTime(user.last_login_at) : <span style={{ color: 'var(--faint)' }}>never</span>}</td>
                  <td>
                    {user.is_active ? (
                      <span className="chip" style={{ color: 'var(--good)', background: 'var(--sunken)' }}>
                        <span className="dot" />
                        Active
                      </span>
                    ) : (
                      <span className="chip chip-dup" title="Cannot sign in">
                        <span className="dot" />
                        Deactivated
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(user)}>Edit</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setPermissionsFor(user)}>
                      Permissions
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setResetting(user)}>Password</button>
                    {reportable(user) && (
                      <button className="btn btn-ghost btn-sm" onClick={() => setReportFor([user])}>Report</button>
                    )}
                    {/* Deactivating yourself, or the last admin, is blocked by
                        the API — don't offer the button either. */}
                    {user.id !== me?.id && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={user.is_active ? { color: 'var(--danger)' } : { color: 'var(--good)' }}
                        onClick={() => setConfirmToggle(user)}
                      >
                        {user.is_active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    )}
                    {user.id !== me?.id && !user.is_active && user.lead_count === 0 && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={async () => {
                          if (!confirm(`Permanently remove ${user.full_name}?`)) return;
                          try {
                            await deleteUser.mutateAsync(user.id);
                          } catch (err) {
                            setError(err);
                          }
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>

        {!isPending && users.length === 0 && (
          <EmptyState title="No users yet" action={<button className="btn btn-primary" onClick={() => setCreating(true)}>Add the first user</button>}>
            Add team specialists and managers so leads can be assigned to them.
          </EmptyState>
        )}
      </div>

      <RecentReports />

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-head"><h2>What each role can do</h2></div>
        <div className="card-body" style={{ paddingTop: 8 }}>
          <table className="data">
            <thead>
              <tr><th>Role</th><th>Sees</th><th>Permissions</th><th className="num">Users</th></tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td><strong>{role.name}</strong></td>
                  <td>{SCOPE_HELP[role.data_scope] ?? role.data_scope}</td>
                  <td>{role.permission_count} permissions</td>
                  <td className="num">{role.user_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <UserFormModal onClose={() => setCreating(false)} />}
      {editing && <UserFormModal user={editing} onClose={() => setEditing(null)} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
      {reportFor && (
        <SendReportModal
          users={reportFor}
          onClose={() => { setReportFor(null); setSelected(new Set()); }}
        />
      )}
      {permissionsFor && (
        <UserPermissionsModal user={permissionsFor} onClose={() => setPermissionsFor(null)} />
      )}
      {confirmToggle && (
        <Modal
          title={confirmToggle.is_active ? `Deactivate ${confirmToggle.full_name}?` : `Reactivate ${confirmToggle.full_name}?`}
          onClose={() => setConfirmToggle(null)}
          width={500}
          footer={
            <>
              <button className="btn" onClick={() => setConfirmToggle(null)}>Cancel</button>
              <button
                className={confirmToggle.is_active ? 'btn btn-danger' : 'btn btn-primary'}
                disabled={updateUser.isPending}
                onClick={() => toggleActive(confirmToggle)}
              >
                {updateUser.isPending
                  ? <span className="spinner" />
                  : confirmToggle.is_active ? 'Deactivate' : 'Reactivate'}
              </button>
            </>
          }
        >
          {confirmToggle.is_active ? (
            <>
              <p style={{ marginTop: 0 }}>
                <strong>{confirmToggle.full_name}</strong> will be signed out immediately and will not
                be able to log in again.
              </p>
              <ul style={{ marginBottom: 0 }}>
                <li>Any open session stops working on their next click — not in 15 minutes.</li>
                <li>Their account, history and notes are kept.</li>
                {confirmToggle.lead_count > 0 && (
                  <li>
                    <strong>
                      {confirmToggle.lead_count} lead{confirmToggle.lead_count === 1 ? '' : 's'} stay
                      assigned to them
                    </strong>{' '}
                    and nobody will be working those — reassign them afterwards.
                  </li>
                )}
                <li>You can reactivate them at any time.</li>
              </ul>
            </>
          ) : (
            <p style={{ margin: 0 }}>
              <strong>{confirmToggle.full_name}</strong> will be able to log in again with their
              existing password, and returns to the {confirmToggle.role?.name ?? 'their'} role with the
              same permissions as before.
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function UserFormModal({ user, onClose }: { user?: CrmUser; onClose: () => void }) {
  const isEdit = Boolean(user);
  const { data: roles = [] } = useRoles();
  const { data: teams = [] } = useTeams();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [password, setPassword] = useState(isEdit ? '' : suggestPassword());
  const [roleId, setRoleId] = useState(user?.role?.id ?? '');
  // One team per user (backend migration 007), so this is a single id rather
  // than a set — the API still takes an array.
  const [teamId, setTeamId] = useState<string>(user?.teams[0]?.id ?? '');
  const [error, setError] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const selectedRole = roles.find((r) => r.id === roleId);
  const busy = createUser.isPending || updateUser.isPending;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    try {
      if (isEdit && user) {
        await updateUser.mutateAsync({
          id: user.id,
          full_name: fullName.trim(),
          phone: phone.trim() || null,
          role_id: roleId,
          team_ids: teamId ? [teamId] : [],
        });
      } else {
        await createUser.mutateAsync({
          full_name: fullName.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          password,
          role_id: roleId,
          team_ids: teamId ? [teamId] : [],
        });
      }
      onClose();
    } catch (err) {
      setError(err);
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
    }
  }

  return (
    <Modal
      title={isEdit ? `Edit ${user!.full_name}` : 'Add user'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="user-form" disabled={busy || !roleId}>
            {busy ? <span className="spinner" /> : isEdit ? 'Save changes' : 'Create user'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form id="user-form" onSubmit={submit}>
        <div className="grid-2">
          <div className={`field${fieldErrors.full_name ? ' invalid' : ''}`}>
            <label htmlFor="u-name">Full name<span className="req">*</span></label>
            <input id="u-name" required autoFocus value={fullName} onChange={(e) => setFullName(e.target.value)} />
            {fieldErrors.full_name && <div className="err">{fieldErrors.full_name}</div>}
          </div>
          <div className={`field${fieldErrors.email ? ' invalid' : ''}`}>
            <label htmlFor="u-email">Email<span className="req">*</span></label>
            <input
              id="u-email"
              type="email"
              required
              value={email}
              disabled={isEdit}
              onChange={(e) => setEmail(e.target.value)}
            />
            {/* The email is the login identity and is referenced across audit
                history, so it is fixed after creation. */}
            {isEdit && <div className="hint">Cannot be changed after creation</div>}
            {fieldErrors.email && <div className="err">{fieldErrors.email}</div>}
          </div>
        </div>

        <div className="field">
          <label htmlFor="u-phone">Phone</label>
          <input id="u-phone" type="tel" placeholder="98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>

        {!isEdit && (
          <div className={`field${fieldErrors.password ? ' invalid' : ''}`}>
            <label htmlFor="u-pass">Initial password<span className="req">*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="u-pass" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" className="btn" onClick={() => setPassword(suggestPassword())}>Regenerate</button>
            </div>
            <div className="hint">
              At least 10 characters. Share it with them securely — they can't change it themselves yet.
            </div>
            {fieldErrors.password && <div className="err">{fieldErrors.password}</div>}
          </div>
        )}

        <div className="field">
          <label htmlFor="u-role">Role<span className="req">*</span></label>
          <select id="u-role" required value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">Select a role…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          {selectedRole && (
            <div className="hint">
              {SCOPE_HELP[selectedRole.data_scope]} · {selectedRole.permission_count} permissions
            </div>
          )}
        </div>

        {teams.length > 0 && (
          <div className="field">
            <label htmlFor="u-team">Team</label>
            <select id="u-team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">No team</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                  {team.manager ? ` — under ${team.manager.full_name}` : ''}
                </option>
              ))}
            </select>
            {selectedRole?.data_scope === 'team' ? (
              <div className="hint" style={{ color: 'var(--warn)' }}>
                A manager is not a member of the team they run — set them as its manager from
                Teams instead, or they will be skipped by their own auto-distribute.
              </div>
            ) : (
              <div className="hint">
                One team at a time: a team specialist sits under exactly one manager, so they never
                appear in two lead rotations. Changing this moves them.
              </div>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */

function ResetPasswordModal({ user, onClose }: { user: CrmUser; onClose: () => void }) {
  const reset = useResetPassword();
  const [password, setPassword] = useState(suggestPassword());
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await reset.mutateAsync({ id: user.id, password });
      setDone(true);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Modal
      title={`Reset password — ${user.full_name}`}
      onClose={onClose}
      width={480}
      footer={
        done ? (
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        ) : (
          <>
            <button className="btn" type="button" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" form="reset-form" disabled={reset.isPending}>
              {reset.isPending ? <span className="spinner" /> : 'Set password'}
            </button>
          </>
        )
      }
    >
      <ErrorBanner error={error} />
      {done ? (
        <div>
          <p>Password updated. Share this with {user.full_name} securely — it is shown once and is not recoverable.</p>
          <div className="mono" style={{ padding: 12, background: 'var(--sunken)', border: '1px solid var(--rule)', borderRadius: 6, fontSize: 15, textAlign: 'center' }}>
            {password}
          </div>
        </div>
      ) : (
        <form id="reset-form" onSubmit={submit}>
          <div className="field">
            <label htmlFor="r-pass">New password</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="r-pass" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" className="btn" onClick={() => setPassword(suggestPassword())}>Regenerate</button>
            </div>
            <div className="hint">Their existing sessions stay valid until their tokens expire.</div>
          </div>
        </form>
      )}
    </Modal>
  );
}
